/**
 * Memory Loop — короткий цикл освоения слова.
 *
 * Увидеть → Разбить → Скрыть → Напечатать → Применить → Вернуть
 * (ТЗ FR-EN-07, §7.4). Цикл проходит каждое новое или проблемное слово.
 *
 * Подсказки исчезают поэтапно, ступенью на стадию (ТЗ FR-EN-08, §7.5):
 * уровень задаёт `hintLevelFor`, и другого источника у него нет — иначе
 * лестница и цикл разошлись бы.
 * Цикл описан как конечный автомат: переход вперёд только при успехе, откат
 * на шаг назад при ошибке, а не сброс к началу.
 */

import type { HintLevel } from '@type2x/shared-contracts';

/** Стадии цикла. */
export const MEMORY_LOOP_STAGES = ['see', 'split', 'hide', 'type', 'apply', 'return'] as const;
export type MemoryLoopStage = (typeof MEMORY_LOOP_STAGES)[number];

/** Уровень подсказок, соответствующий стадии. */
const HINT_BY_STAGE: Readonly<Record<MemoryLoopStage, HintLevel>> = {
  see: 'full-keyboard-and-finger',
  split: 'full-keyboard-and-finger',
  hide: 'zone-highlight',
  type: 'keyboard-hidden',
  apply: 'keyboard-hidden',
  return: 'no-hints',
};

/** Состояние цикла для одного слова. */
export interface MemoryLoopState {
  readonly word: string;
  readonly stage: MemoryLoopStage;
  /** Сколько раз слово уже показывалось подряд. */
  readonly consecutiveShows: number;
}

/** Начальное состояние цикла. */
export function startMemoryLoop(word: string): MemoryLoopState {
  return { word, stage: 'see', consecutiveShows: 0 };
}

/** Уровень подсказок текущей стадии. */
export function hintLevelFor(stage: MemoryLoopStage): HintLevel {
  return HINT_BY_STAGE[stage];
}

function stageIndex(stage: MemoryLoopStage): number {
  return MEMORY_LOOP_STAGES.indexOf(stage);
}

/**
 * Переводит цикл на следующую стадию.
 *
 * @param state текущее состояние;
 * @param success выполнено ли задание стадии без ошибок.
 */
export function advanceMemoryLoop(state: MemoryLoopState, success: boolean): MemoryLoopState {
  const index = stageIndex(state.stage);
  const nextIndex = success
    ? Math.min(index + 1, MEMORY_LOOP_STAGES.length - 1)
    : Math.max(index - 1, 0);

  return {
    word: state.word,
    stage: MEMORY_LOOP_STAGES[nextIndex]!,
    consecutiveShows: state.consecutiveShows + 1,
  };
}

/** Завершён ли цикл: слово дошло до стадии отложенного возврата. */
export function isMemoryLoopComplete(state: MemoryLoopState): boolean {
  return state.stage === 'return';
}

/**
 * Делит слово на естественные части для стадии «Разбить».
 *
 * Используется простое слоговое приближение по гласным: оно не претендует на
 * лингвистическую точность, но даёт зрительно осмысленные куски, которые
 * запоминаются как моторные шаблоны.
 */
export function splitIntoChunks(word: string, vowels: ReadonlySet<string>): string[] {
  const characters = [...word];
  if (characters.length <= 3) return [word];

  const chunks: string[] = [];
  let current = '';
  let seenVowel = false;

  for (const character of characters) {
    current += character;
    const isVowel = vowels.has(character.toLowerCase());

    if (isVowel) seenVowel = true;
    else if (seenVowel && current.length >= 2) {
      // Согласная после гласной закрывает слог.
      chunks.push(current);
      current = '';
      seenVowel = false;
    }
  }

  if (current !== '') {
    if (chunks.length > 0 && current.length === 1) {
      chunks[chunks.length - 1] += current;
    } else {
      chunks.push(current);
    }
  }

  return chunks;
}

/** Гласные русского языка. */
export const RU_VOWELS: ReadonlySet<string> = new Set([...'аеёиоуыэюя']);

/** Гласные английского языка. */
export const EN_VOWELS: ReadonlySet<string> = new Set([...'aeiouy']);
